/**
 * Typed client for the secure project-aware language-service gateway.
 *
 * Browser fallback: when health/capabilities are unavailable or report stub-only
 * adapters (`implemented: false` / `processesEnabled: false`), the Code Editor
 * must stay on Monaco-only intelligence (built-in workers / client formatters).
 * Do not block editing or saving when the LSP gateway is offline.
 */
import { apiFetch, readErrorMessage, readJson } from '../../api/client';

export const CODE_LANGUAGE_SERVICE_BASE = '/api/code-language-service';
export const CODE_LANGUAGE_SERVICE_HUB = '/hubs/code-language-service';

export interface LanguageServiceCapabilitiesDto {
  languageId: string;
  adapterName: string;
  completion: boolean;
  hover: boolean;
  definition: boolean;
  references: boolean;
  rename: boolean;
  documentSymbols: boolean;
  diagnostics: boolean;
  codeActions: boolean;
  format: boolean;
  projectAware: boolean;
  implemented: boolean;
}

export interface LanguageServiceHealthDto {
  status: string;
  processesEnabled: boolean;
  secureMode: boolean;
  allowedLanguages: string[];
  allowedAdapters: string[];
  capabilities: LanguageServiceCapabilitiesDto[];
  detail: string;
}

export interface LanguageServiceSessionScope {
  userId: string;
  mountId: string;
  workspaceRelativePath: string;
  languageId: string;
}

export interface LanguageServicePosition {
  line: number;
  character: number;
}

/** True when the browser should use Monaco-only features (no live server LSP). */
export function shouldUseMonacoOnlyFallback(health: LanguageServiceHealthDto | null | undefined): boolean {
  if (!health) return true;
  if (health.processesEnabled) return false;
  if (!health.capabilities?.length) return true;
  return !health.capabilities.some((c) => c.implemented);
}

export async function fetchCodeLanguageServiceHealth(): Promise<LanguageServiceHealthDto> {
  const res = await apiFetch(`${CODE_LANGUAGE_SERVICE_BASE}/health`);
  if (!res.ok) {
    throw new Error(await readErrorMessage(res) || 'Language service health failed.');
  }
  return readJson<LanguageServiceHealthDto>(res);
}

export async function fetchCodeLanguageServiceCapabilities(): Promise<LanguageServiceCapabilitiesDto[]> {
  const res = await apiFetch(`${CODE_LANGUAGE_SERVICE_BASE}/capabilities`);
  if (!res.ok) {
    throw new Error(await readErrorMessage(res) || 'Language service capabilities failed.');
  }
  return readJson<LanguageServiceCapabilitiesDto[]>(res);
}

/** Placeholder session methods — wire to REST/SignalR when adapters become implemented. */
export const codeLanguageServiceSession = {
  async open(_scope: LanguageServiceSessionScope, _uri: string, _text: string): Promise<void> {
    // Intentionally unused until server adapters are implemented.
  },

  async change(_scope: LanguageServiceSessionScope, _uri: string, _text: string): Promise<void> {
    // Intentionally unused until server adapters are implemented.
  },

  async close(_scope: LanguageServiceSessionScope, _uri: string): Promise<void> {
    // Intentionally unused until server adapters are implemented.
  },

  async completion(
    _scope: LanguageServiceSessionScope,
    _uri: string,
    _position: LanguageServicePosition,
  ): Promise<unknown[]> {
    return [];
  },
};
